import { useEffect, useRef, useState } from 'react';

// ── Session colors (solid hex) ────────────────────────────────────────────────
export const BG_MAIN      = '#0F0F0F';
export const BG_PREMARKET = '#523A15';
export const BG_POSTMKT   = '#131C33';

// Timeframes shown in both charts
export const TIMEFRAMES = ['1m', '5m', '15m', '30m', '1h', '4h', '1D'];


// Session coloring is only meaningful on intraday timeframes
export const SESSION_TF = new Set(['1m', '5m', '15m', '30m', '1h']);

// ── ET time formatters (reused across multiple charts) ────────────────────────
const etTimeFmt  = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false });
const etDateFmt  = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' });
const etMonthFmt = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', year: 'numeric' });
const etYearFmt  = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric' });
const etFullFmt  = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });

// ── Session classification (ET) ───────────────────────────────────────────────
export function getSessionType(unixTs) {
  const d  = new Date(unixTs * 1000);
  const et = new Date(d.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const m  = et.getHours() * 60 + et.getMinutes();
  if (m >= 570 && m < 960)  return 'regular';
  if (m >= 240 && m < 570)  return 'premarket';
  if (m >= 960 && m < 1200) return 'postmarket';
  return null;
}

// ── Session filter (TradingView-style "Session" chooser) ──────────────────────
// 'full'    = pre-market + regular + post-market (excludes the overnight gap)
// 'regular' = 09:30-16:00 ET only
// 'global'  = every bar the server returned, including the overnight session
//             (Cboe Global Trading Hours) — the historical default, unfiltered.
export const SESSIONS = [
  { key: 'full',    label: 'Full trading hours',    badge: 'FULL' },
  { key: 'regular', label: 'Regular trading hours', badge: 'RTH' },
  { key: 'global',  label: 'Global trading hours',  badge: 'GLOBAL' },
];

export function sessionAllows(session, unixTs) {
  if (session === 'global') return true;
  const t = getSessionType(unixTs);
  if (session === 'regular') return t === 'regular';
  return t !== null; // 'full'
}

export function filterBySession(bars, session) {
  if (session === 'global' || !bars) return bars;
  return bars.filter(b => sessionAllows(session, b.time));
}

// ── Day separator line timestamps ─────────────────────────────────────────────
// Returns the timestamp of the FIRST bar in each new ET calendar day.
// Works for any array with a .time property (bars, cap-series points, etc.).
export function buildDayLines(items) {
  if (!items || !items.length) return [];
  const lines = [];
  let prevDate = null;
  for (const item of items) {
    const etDate = new Date(item.time * 1000).toLocaleDateString('en-US', { timeZone: 'America/New_York' });
    if (prevDate !== null && etDate !== prevDate) lines.push(item.time);
    prevDate = etDate;
  }
  return lines;
}

// ── Day line primitive — draws a thin vertical rule at each day boundary ──────
// Attach AFTER any session-band primitive so the line renders on top of bands.
export class DayLinePrimitive {
  constructor() {
    this._times = [];
    this._chart = null;
    this._requestUpdate = null;
    this._cachedXs = []; // pixel positions computed in updateAllViews
  }
  setLines(times) { this._times = times; if (this._requestUpdate) this._requestUpdate(); }
  clearLines()    { this._times = []; this._cachedXs = []; if (this._requestUpdate) this._requestUpdate(); }
  attached({ chart, requestUpdate }) { this._chart = chart; this._requestUpdate = requestUpdate; }
  detached()      { this._chart = null; this._requestUpdate = null; this._cachedXs = []; }

  // Called when data or viewport changes — NOT on every crosshair move.
  // Pre-compute pixel positions here so drawBackground stays allocation-free.
  updateAllViews() {
    if (!this._chart || !this._times.length) { this._cachedXs = []; return; }
    const ts = this._chart.timeScale();
    const xs = [];
    for (const t of this._times) {
      const x = ts.timeToCoordinate(t);
      if (x !== null) xs.push(x);
    }
    this._cachedXs = xs;
  }

  paneViews() { return [this._view()]; }
  _view() {
    const self = this;
    return {
      zOrder: () => 'bottom',
      renderer: () => ({
        draw: () => {},
        drawBackground: (target) => {
          if (!self._cachedXs.length) return;
          target.useMediaCoordinateSpace(scope => {
            const ctx = scope.context;
            const W   = scope.mediaSize.width;
            const H   = scope.mediaSize.height;
            ctx.save();
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
            ctx.lineWidth   = 1;
            for (const x of self._cachedXs) {
              if (x < 0 || x > W) continue;
              ctx.beginPath();
              ctx.moveTo(x, 0);
              ctx.lineTo(x, H);
              ctx.stroke();
            }
            ctx.restore();
          });
        },
      }),
    };
  }
}

// ── Build session bands from bar/point array (needs .time) ───────────────────
// fitContent: true  → last band ends at bandEnd (not +60) so timeToCoordinate()
//             returns a valid pixel on fitContent() charts; prevents the null→W
//             fallback that made the last session color fill the entire canvas.
export function buildSessionBands(bars, { fitContent = false } = {}) {
  const bands = [];
  if (!bars || !bars.length) return bands;

  let curType = null, bandStart = null, bandEnd = null;

  const flush = (isLast = false) => {
    if (curType !== null) {
      bands.push({ type: curType, start: bandStart, end: isLast && fitContent ? bandEnd : bandEnd + 60 });
    }
  };

  for (const bar of bars) {
    const t = getSessionType(bar.time);
    if (t !== curType) { flush(); curType = t; bandStart = bar.time; }
    bandEnd = bar.time;
  }
  flush(true);
  return bands;
}

// ── Lightweight Charts v5 ISeriesPrimitive for session backgrounds ─────────────
//
//  IPrimitivePaneRenderer.drawBackground(target) renders behind all series
//  content. target.useMediaCoordinateSpace() gives CSS-pixel canvas context.
//
export class SessionBandPrimitive {
  constructor() {
    this._bands = [];
    this._chart = null;
    this._requestUpdate = null;
    this._cachedRects = []; // { color, x1, x2 } computed in updateAllViews
  }

  setBands(bands) {
    this._bands = bands;
    if (this._requestUpdate) this._requestUpdate();
  }

  clearBands() {
    this._bands = [];
    this._cachedRects = [];
    if (this._requestUpdate) this._requestUpdate();
  }

  attached({ chart, requestUpdate }) {
    this._chart = chart;
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this._chart = null;
    this._requestUpdate = null;
    this._cachedRects = [];
  }

  // Called when data or viewport changes — NOT on every crosshair move.
  // Pre-compute pixel positions here so drawBackground stays allocation-free.
  updateAllViews() {
    if (!this._chart || !this._bands.length) { this._cachedRects = []; return; }
    const ts = this._chart.timeScale();
    const rects = [];
    for (let bi = 0; bi < this._bands.length; bi++) {
      const band = this._bands[bi];
      const color =
        band.type === 'premarket'  ? BG_PREMARKET :
        band.type === 'postmarket' ? BG_POSTMKT   : BG_MAIN;

      let x1 = ts.timeToCoordinate(band.start);
      let x2 = ts.timeToCoordinate(band.end);

      // band.end may fall in a data gap (e.g. SPXW 9:15 AM → 9:30 AM gap).
      // Fall back to the next band's start so this band doesn't bleed to W.
      if (x2 === null && bi + 1 < this._bands.length) {
        x2 = ts.timeToCoordinate(this._bands[bi + 1].start);
      }

      if (x1 === null && x2 === null) continue;
      rects.push({ color, x1, x2 });
    }
    this._cachedRects = rects;
  }

  paneViews() { return [this._view()]; }

  _view() {
    const self = this;
    return {
      zOrder: () => 'bottom',
      renderer: () => ({
        draw: () => {},
        drawBackground: (target) => {
          if (!self._cachedRects.length) return;
          target.useMediaCoordinateSpace(scope => {
            const ctx = scope.context;
            const W   = scope.mediaSize.width;
            const H   = scope.mediaSize.height;
            for (const rect of self._cachedRects) {
              const left  = rect.x1 !== null ? Math.max(0, rect.x1) : 0;
              const right = rect.x2 !== null ? Math.min(W, rect.x2) : W;
              if (right <= left) continue;
              ctx.fillStyle = rect.color;
              ctx.fillRect(left, 0, right - left, H);
            }
          });
        },
      }),
    };
  }
}

// ── Shared chart creation options ─────────────────────────────────────────────
export function baseChartOptions(width) {
  return {
    layout: {
      background: { type: 'solid', color: BG_MAIN },
      textColor:  '#9ca3af',
      fontSize:   11,
    },
    grid: {
      vertLines: { color: '#1a1a1a' },
      horzLines: { color: '#1a1a1a' },
    },
    rightPriceScale: { borderColor: '#2d2d2d' },
    timeScale: {
      borderColor:    '#2d2d2d',
      timeVisible:    true,
      secondsVisible: false,
      rightOffset:    8,
      // Display all tick labels in US Eastern Time (24-hour)
      tickMarkFormatter: (time, tickMarkType) => {
        const d = new Date(time * 1000);
        if (tickMarkType >= 3) return etTimeFmt.format(d);          // HH:MM ET
        if (tickMarkType === 2) return etDateFmt.format(d);         // "Jun 26"
        if (tickMarkType === 1) return etMonthFmt.format(d);        // "Jun 2026"
        return etYearFmt.format(d);                                  // "2026"
      },
    },
    // Crosshair tooltip time in US Eastern Time (24-hour)
    localization: {
      timeFormatter: (time) => etFullFmt.format(new Date(time * 1000)),
    },
    crosshair: {
      mode: 0, // 0 = Normal (free movement), 1 = Magnet (snaps to close)
      vertLine: { color: '#555', width: 1, style: 0 },
      horzLine: { color: '#555', width: 1, style: 0 },
    },
    handleScroll: {
      mouseWheel:       true,
      pressedMouseMove: true,
      horzTouchDrag:    true,
      vertTouchDrag:    true,
    },
    handleScale: {
      mouseWheel: true,
      pinch:      true,
    },
    width,
    height: 400,
  };
}

// ── Timeframe button render helper ────────────────────────────────────────────
export function TfButtons({ tf, setTf }) {
  return (
    <div className="flex items-center gap-1">
      {TIMEFRAMES.map(t => (
        <button
          key={t}
          onClick={() => setTf(t)}
          className="text-xs font-medium px-2 py-1 rounded transition-colors"
          style={
            t === tf
              ? { backgroundColor: '#60a5fa22', color: '#60a5fa', border: '1px solid #60a5fa55' }
              : { backgroundColor: 'transparent', color: '#6b7280', border: '1px solid transparent' }
          }
        >
          {t}
        </button>
      ))}
    </div>
  );
}

// ── Price-scale + chart-area vertical interactions ────────────────────────────
//
// Chart area drag (left mouse hold + move):
//   Horizontal = LWC built-in (pressedMouseMove).
//   Vertical   = this code shifts the price range up/down (pan, not zoom).
//   Both work simultaneously for full 2D pan. A 5px vertical deadzone
//   prevents plain clicks from locking the range.
//
// Price scale (right numbers):
//   Drag up/down = zoom price axis (taller/shorter candles, same time window).
//   Mouse wheel  = same zoom (does NOT zoom the time axis).
//
// Double-click anywhere = reset to auto-scale.
// Returns { detach, reset }.
export function attachVerticalPan(el, seriesList) {
  if (!seriesList || seriesList.length === 0) return { detach: () => {}, reset: () => {} };

  const ref = seriesList[0];
  const PS_W = 60;
  let lockedMin = null, lockedMax = null;

  // --- shared helpers ---
  const inPriceScale = (e) => {
    const rect = el.getBoundingClientRect();
    return (e.clientX - rect.left) >= (rect.width - PS_W);
  };

  const getRange = () => {
    const h = el.clientHeight || 400;
    let max = null, min = null;
    for (let y = 2; y < Math.min(40, h); y++) {
      const p = ref.coordinateToPrice(y);
      if (p !== null) { max = p; break; }
    }
    for (let y = h - 2; y > Math.max(h - 80, 10); y--) {
      const p = ref.coordinateToPrice(y);
      if (p !== null) { min = p; break; }
    }
    return (max !== null && min !== null && max > min) ? { min, max } : null;
  };

  const currentRange = () => {
    if (lockedMin !== null) return { min: lockedMin, max: lockedMax };
    return getRange();
  };

  const setRange = (min, max) => {
    lockedMin = min; lockedMax = max;
    for (const s of seriesList) {
      s.applyOptions({ autoscaleInfoProvider: () => ({ priceRange: { minValue: min, maxValue: max } }) });
    }
  };

  const reset = () => {
    lockedMin = null; lockedMax = null;
    for (const s of seriesList) {
      s.applyOptions({ autoscaleInfoProvider: (getDefault) => getDefault() });
    }
  };

  // --- state ---
  let mode = null; // null | 'chart-pan' | 'ps-zoom'
  let startY = 0, startMin = null, startMax = null;
  let chartEngaged = false; // true once 5px vertical deadzone is passed

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    startY = e.clientY;
    chartEngaged = false;
    const r = currentRange();
    if (!r) return;
    startMin = r.min; startMax = r.max;
    mode = inPriceScale(e) ? 'ps-zoom' : 'chart-pan';
  };

  const onMouseMove = (e) => {
    if (!mode || startMin === null) return;
    const deltaY = e.clientY - startY;

    if (mode === 'chart-pan') {
      if (!chartEngaged && Math.abs(deltaY) < 5) return;
      chartEngaged = true;
      // Shift the price range (pan) — same range size, different position.
      const h = el.clientHeight || 400;
      const rangeSize = startMax - startMin;
      const shift = (deltaY / h) * rangeSize;
      setRange(startMin + shift, startMax + shift);
    } else {
      // Price-scale drag: zoom (change range size, keep center).
      const h = el.clientHeight || 400;
      const factor = 1 + (deltaY / h) * 2;
      if (factor <= 0.05) return;
      const mid = (startMin + startMax) / 2;
      const newHalf = ((startMax - startMin) * factor) / 2;
      setRange(mid - newHalf, mid + newHalf);
    }
  };

  const onMouseUp = () => { mode = null; chartEngaged = false; };

  // Price-scale wheel = zoom price axis only (block LWC time zoom).
  const onWheel = (e) => {
    if (!inPriceScale(e)) return;
    e.preventDefault();
    e.stopPropagation();
    const r = currentRange();
    if (!r) return;
    const rangeSize = r.max - r.min;
    if (rangeSize <= 0) return;
    const mid = (r.min + r.max) / 2;
    const factor = e.deltaY > 0 ? 1.15 : 0.87;
    const newHalf = (rangeSize * factor) / 2;
    setRange(mid - newHalf, mid + newHalf);
  };

  const onDblClick = () => reset();

  el.addEventListener('mousedown', onMouseDown);
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
  el.addEventListener('wheel', onWheel, { passive: false });
  el.addEventListener('dblclick', onDblClick);

  const detach = () => {
    el.removeEventListener('mousedown', onMouseDown);
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    el.removeEventListener('wheel', onWheel);
    el.removeEventListener('dblclick', onDblClick);
  };

  return { detach, reset };
}

// ── Measure tool (TradingView-style drag ruler) ───────────────────────────────
//
// lightweight-charts has no built-in ruler, so this is a v5 ISeriesPrimitive
// (pure renderer, driven by pixel coords + precomputed stats) plus
// attachMeasureTool() below (pointer-event drag gesture that computes those
// stats via the chart/series coordinate APIs). Pointer events (not mouse
// events) so it works with touch on mobile too.
export class MeasurePrimitive {
  constructor() {
    this._m = null; // { x1,y1,x2,y2, priceDiff, pricePct, barCount, timeLabel, up }
    this._requestUpdate = null;
  }
  attached({ requestUpdate }) { this._requestUpdate = requestUpdate; }
  detached() { this._requestUpdate = null; }
  setRange(m) { this._m = m; this._requestUpdate?.(); }
  clear() { this._m = null; this._requestUpdate?.(); }
  updateAllViews() {}
  paneViews() { return [this._view()]; }
  _view() {
    const self = this;
    return {
      zOrder: () => 'top',
      renderer: () => ({
        draw: (target) => {
          const m = self._m;
          if (!m) return;
          target.useMediaCoordinateSpace(scope => {
            const ctx = scope.context;
            const color = m.up ? '#22c55e' : '#ef4444';
            const left = Math.min(m.x1, m.x2), right = Math.max(m.x1, m.x2);
            const top = Math.min(m.y1, m.y2), bottom = Math.max(m.y1, m.y2);

            ctx.save();
            ctx.fillStyle = m.up ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)';
            ctx.fillRect(left, top, right - left, bottom - top);
            ctx.strokeStyle = color;
            ctx.lineWidth = 1;
            ctx.strokeRect(left, top, right - left, bottom - top);

            ctx.beginPath();
            ctx.moveTo(m.x1, m.y1);
            ctx.lineTo(m.x2, m.y2);
            ctx.stroke();
            for (const [px, py] of [[m.x1, m.y1], [m.x2, m.y2]]) {
              ctx.beginPath();
              ctx.arc(px, py, 3, 0, 2 * Math.PI);
              ctx.fillStyle = color;
              ctx.fill();
            }

            const line1 = `${m.priceDiff >= 0 ? '+' : ''}${m.priceDiff.toFixed(2)} (${m.pricePct >= 0 ? '+' : ''}${m.pricePct.toFixed(2)}%)`;
            const line2 = m.timeLabel;
            ctx.font = '11px sans-serif';
            const w = Math.max(ctx.measureText(line1).width, ctx.measureText(line2).width) + 16;
            let boxX = m.x2 + 10;
            if (boxX + w > scope.mediaSize.width) boxX = m.x2 - w - 10;
            const boxY = Math.max(4, Math.min(m.y2 - 20, scope.mediaSize.height - 44));

            ctx.fillStyle = '#111827';
            ctx.strokeStyle = color;
            ctx.fillRect(boxX, boxY, w, 36);
            ctx.strokeRect(boxX, boxY, w, 36);
            ctx.fillStyle = color;
            ctx.fillText(line1, boxX + 8, boxY + 15);
            ctx.fillStyle = '#9ca3af';
            ctx.fillText(line2, boxX + 8, boxY + 29);
            ctx.restore();
          });
        },
      }),
    };
  }
}

function fmtTimeSpan(sec) {
  const s = Math.abs(Math.round(sec));
  const sign = sec < 0 ? '-' : '';
  if (s < 60) return `${sign}${s}s`;
  if (s < 3600) return `${sign}${Math.round(s / 60)}m`;
  if (s < 86400) return `${sign}${(s / 3600).toFixed(1)}h`;
  return `${sign}${(s / 86400).toFixed(1)}d`;
}

// Attaches the drag gesture to `container` and the render primitive to
// `series`. Only active (drag starts a measurement) while setActive(true).
// Suppresses the chart's own pan/zoom during a measure-drag so they don't
// fight over the same gesture. Returns { setActive, detach }.
export function attachMeasureTool(chart, series, container) {
  const primitive = new MeasurePrimitive();
  series.attachPrimitive(primitive);

  const RESTORE_SCROLL = { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: true };
  const RESTORE_SCALE = { mouseWheel: true, pinch: true };

  let active = false;
  let dragging = false;
  let startPx = null;
  let startData = null;

  const relPos = (e) => {
    const rect = container.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const dataAt = (pos) => {
    const ts = chart.timeScale();
    return {
      time: ts.coordinateToTime(pos.x),
      logical: ts.coordinateToLogical(pos.x),
      price: series.coordinateToPrice(pos.y),
    };
  };

  const onPointerDown = (e) => {
    if (!active || e.button !== 0) return;
    const pos = relPos(e);
    const d = dataAt(pos);
    if (d.price === null || d.time === null) { primitive.clear(); return; }
    dragging = true;
    startPx = pos;
    startData = d;
    chart.applyOptions({ handleScroll: false, handleScale: false });
    e.stopPropagation();
    e.preventDefault();
  };

  const onPointerMove = (e) => {
    if (!active || !dragging) return;
    const pos = relPos(e);
    const d = dataAt(pos);
    if (d.price === null) return;
    const priceDiff = d.price - startData.price;
    const pricePct = startData.price !== 0 ? (priceDiff / Math.abs(startData.price)) * 100 : 0;
    const barCount = (d.logical !== null && startData.logical !== null)
      ? Math.round(d.logical - startData.logical) : 0;
    const timeDiffSec = (d.time != null && startData.time != null) ? (d.time - startData.time) : 0;
    primitive.setRange({
      x1: startPx.x, y1: startPx.y, x2: pos.x, y2: pos.y,
      priceDiff, pricePct, barCount,
      timeLabel: `${barCount >= 0 ? '' : '-'}${Math.abs(barCount)} bars, ${fmtTimeSpan(timeDiffSec)}`,
      up: priceDiff >= 0,
    });
    e.stopPropagation();
    e.preventDefault();
  };

  const onPointerUp = () => {
    if (!dragging) return;
    dragging = false;
    chart.applyOptions({ handleScroll: RESTORE_SCROLL, handleScale: RESTORE_SCALE });
  };

  container.addEventListener('pointerdown', onPointerDown, { capture: true });
  document.addEventListener('pointermove', onPointerMove);
  document.addEventListener('pointerup', onPointerUp);

  const setActive = (val) => {
    active = val;
    container.style.cursor = val ? 'crosshair' : '';
    if (!val) {
      dragging = false;
      primitive.clear();
      chart.applyOptions({ handleScroll: RESTORE_SCROLL, handleScale: RESTORE_SCALE });
    }
  };

  const detach = () => {
    container.removeEventListener('pointerdown', onPointerDown, { capture: true });
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);
    try { series.detachPrimitive(primitive); } catch { /* series/chart already gone */ }
  };

  return { setActive, detach };
}

// ── Session selector — TradingView-style dropdown (Full/Regular/Global) ───────
export function SessionSelector({ session, setSession }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const current = SESSIONS.find(s => s.key === session) || SESSIONS[SESSIONS.length - 1];

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen(v => !v)}
        title="Choose which trading sessions to display"
        className="text-xs font-medium px-2 py-1 rounded transition-colors flex items-center gap-1"
        style={
          open
            ? { backgroundColor: '#60a5fa22', color: '#60a5fa', border: '1px solid #60a5fa55' }
            : { backgroundColor: 'transparent', color: '#6b7280', border: '1px solid #374151' }
        }
      >
        {current.badge}
      </button>
      {open && (
        <div
          className="absolute left-0 mt-1 z-20 rounded-lg py-1 shadow-lg"
          style={{ backgroundColor: '#111827', border: '1px solid #374151', minWidth: 190 }}
        >
          <div className="text-[10px] font-semibold uppercase tracking-wide px-3 pt-1 pb-1.5"
            style={{ color: '#6b7280' }}>Session</div>
          {SESSIONS.map(s => (
            <button
              key={s.key}
              onClick={() => { setSession(s.key); setOpen(false); }}
              className="block w-full text-left text-xs px-3 py-2 transition-colors"
              style={
                s.key === session
                  ? { backgroundColor: '#1f2937', color: '#e5e7eb' }
                  : { backgroundColor: 'transparent', color: '#9ca3af' }
              }
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Session legend ─────────────────────────────────────────────────────────────
export function SessionLegend() {
  return (
    <div className="ml-auto flex items-center gap-4 text-xs text-gray-500">
      <span className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm"
          style={{ backgroundColor: BG_PREMARKET, border: '1px solid #7a5520' }} />
        Pre-market
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm"
          style={{ backgroundColor: BG_POSTMKT, border: '1px solid #1e2d50' }} />
        Post-market
      </span>
    </div>
  );
}
